import React, { useCallback, useEffect, useState } from "react";
import styles from "../../_css/Login.module.css";
import {
  getSsoAgencyList,
  getSsoAgencyAdmins,
  sendRequestToAdmin,
} from "../../requests";
import { notifyError, notifySuccess } from "../../utils/helper";
import { ChevronLeft, Building2, UserCircle } from "lucide-react";

const STEPS = {
  AGENCY: "agency",
  ADMIN: "admin",
};

const SsoOnboarding = ({ userEmail, authToken, onRequestSent, onExit }) => {
  const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo.png`;

  const [step, setStep] = useState(STEPS.AGENCY);
  const [agencies, setAgencies] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectingAgencyId, setSelectingAgencyId] = useState(null);

  const loadAgencies = useCallback(async () => {
    if (!authToken) return;
    setLoadingAgencies(true);
    try {
      const response = await getSsoAgencyList(authToken);
      if (response?.status === 200) {
        setAgencies(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to load agencies");
    } finally {
      setLoadingAgencies(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  useEffect(() => {
    if (step === STEPS.ADMIN && !selectedAgency?.id) {
      setStep(STEPS.AGENCY);
    }
  }, [step, selectedAgency]);

  const onSelectAgency = async (agency) => {
    if (!authToken || isTransitioning || loadingAdmins) return;

    setSelectingAgencyId(agency.id);
    setSelectedAgency(agency);
    setSelectedAdmin(null);
    setAdmins([]);

    await new Promise((resolve) => setTimeout(resolve, 320));

    setIsTransitioning(true);
    setSelectingAgencyId(null);
    setStep(STEPS.ADMIN);
    setLoadingAdmins(true);

    try {
      const response = await getSsoAgencyAdmins(agency.id, authToken);
      if (response?.status === 200) {
        setAdmins(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message || "Failed to load agency admins",
      );
      setStep(STEPS.AGENCY);
      setSelectedAgency(null);
    } finally {
      setLoadingAdmins(false);
      setIsTransitioning(false);
    }
  };

  const onBackToAgencies = () => {
    setIsTransitioning(false);
    setSelectingAgencyId(null);
    setStep(STEPS.AGENCY);
    setSelectedAgency(null);
    setSelectedAdmin(null);
    setAdmins([]);
  };

  const onSendRequest = async () => {
    if (!selectedAgency?.id) {
      notifyError("Please select an agency");
      return;
    }
    if (!selectedAdmin?.id) {
      notifyError("Please select an agency administrator");
      return;
    }
    if (!userEmail || !authToken) {
      notifyError("Your session has expired. Please sign in again.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await sendRequestToAdmin(
        {
          user_id: Number(selectedAdmin.id),
          email: userEmail,
        },
        authToken,
      );
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || "Request sent successfully");
        onRequestSent?.();
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitle =
    step === STEPS.AGENCY
      ? "Select your agency"
      : "Select an agency administrator";

  const stepSubtitle =
    step === STEPS.AGENCY
      ? "Choose the agency you want to join"
      : selectedAgency
        ? `Administrators at ${selectedAgency.name}`
        : "Choose who should receive your join request";

  return (
    <div
      className={`app-background w-full min-h-screen h-full flex items-center justify-center overflow-y-auto ${styles.loginPage}`}
    >
      <div
        className={`app-background-surface w-full max-w-md px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 ${styles.onboardingShell}`}
      >
        <div className={styles.authForm}>
          <div className={styles.authHeader}>
            <div className={styles.authLogoWrap}>
              <img
                src={LOGO_SRC}
                alt="Company logo"
                className={styles.authLogo}
              />
            </div>
            <p className={styles.authSubtitle}>{stepTitle}</p>
            <hr className={styles.authDivider} />
          </div>

          <div className={styles.onboardingSteps} aria-hidden="true">
            <span
              className={`${styles.onboardingStepDot} ${
                step === STEPS.AGENCY ? styles.onboardingStepDotActive : ""
              } ${selectedAgency ? styles.onboardingStepDotDone : ""}`}
            />
            <span className={styles.onboardingStepLine} />
            <span
              className={`${styles.onboardingStepDot} ${
                step === STEPS.ADMIN ? styles.onboardingStepDotActive : ""
              } ${selectedAdmin ? styles.onboardingStepDotDone : ""}`}
            />
          </div>

          <div key={step} className={styles.onboardingStepPanel}>
            <div className={styles.authPendingNote}>{stepSubtitle}</div>

            {step === STEPS.ADMIN && (
              <button
                type="button"
                onClick={onBackToAgencies}
                disabled={loadingAdmins || isTransitioning}
                className={styles.authBackButton}
              >
                <ChevronLeft size={16} aria-hidden="true" />
                Back to agencies
              </button>
            )}

            <div className={styles.authOptionList}>
              {step === STEPS.AGENCY && loadingAgencies && (
                <div className={styles.onboardingLoading}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={styles.onboardingSkeleton}
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              )}

              {step === STEPS.AGENCY &&
                !loadingAgencies &&
                agencies.length === 0 && (
                  <p className={styles.authOptionEmpty}>
                    No agencies are available right now. Please try again later.
                  </p>
                )}

              {step === STEPS.AGENCY &&
                !loadingAgencies &&
                agencies.map((agency, index) => {
                  const isSelecting = selectingAgencyId === agency.id;
                  return (
                    <button
                      key={agency.id}
                      type="button"
                      onClick={() => onSelectAgency(agency)}
                      disabled={isTransitioning || Boolean(selectingAgencyId)}
                      className={`${styles.authOptionButton} ${styles.onboardingOptionEnter} ${
                        isSelecting ? styles.authOptionButtonSelecting : ""
                      }`}
                      style={{ animationDelay: `${index * 0.06}s` }}
                    >
                      <Building2 size={18} className={styles.authOptionIcon} />
                      <span className={styles.authOptionLabel}>
                        {agency.name}
                      </span>
                    </button>
                  );
                })}

              {step === STEPS.ADMIN && loadingAdmins && (
                <div className={styles.onboardingLoading}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={styles.onboardingSkeleton}
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              )}

              {step === STEPS.ADMIN &&
                !loadingAdmins &&
                admins.length === 0 && (
                  <p className={styles.authOptionEmpty}>
                    No administrators found for this agency.
                  </p>
                )}

              {step === STEPS.ADMIN &&
                !loadingAdmins &&
                admins.map((admin, index) => {
                  const isSelected = selectedAdmin?.id === admin.id;
                  return (
                    <button
                      key={admin.id}
                      type="button"
                      onClick={() => setSelectedAdmin(admin)}
                      className={`${styles.authOptionButton} ${styles.onboardingOptionEnter} ${
                        isSelected ? styles.authOptionButtonSelected : ""
                      }`}
                      style={{ animationDelay: `${index * 0.06}s` }}
                    >
                      <UserCircle size={18} className={styles.authOptionIcon} />
                      <span className={styles.authOptionText}>
                        <span className={styles.authOptionLabel}>
                          {admin.full_name}
                        </span>
                        <span className={styles.authOptionMeta}>
                          {admin.email}
                        </span>
                      </span>
                    </button>
                  );
                })}
            </div>

            {step === STEPS.ADMIN && !loadingAdmins && (
              <div className={styles.onboardingActions}>
                <button
                  type="button"
                  onClick={onSendRequest}
                  disabled={!selectedAgency || !selectedAdmin || submitting}
                  className={`${styles.authSubmit} ${styles.onboardingSubmit}`}
                >
                  {submitting ? "Sending request…" : "Send request"}
                </button>
                <p className={styles.authRequiredNote}>
                  You must select an agency administrator before your account
                  can be activated.
                </p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onExit}
            className={styles.authMicrosoft}
          >
            Back to login
          </button>

          <p className={styles.authFooterNote}>
            Secure login powered by enterprise-grade encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default SsoOnboarding;
